/// <amd-module name="VSS/Features/Markdown/Utils/UrlHelper" />
import * as Utils_String from "VSS/Core/Util/String";
import { isSameOrigin } from "VSS/Platform/Util/Url";

/**
 * Checks if specified URL is an external URL to the current window.
 * If relative URL is provided - returns false.
 * @param url Url to check
 */
export function isExternalUrl(url: string): boolean {
    let isExternal = false;
    try {
        isExternal = isAbsoluteUrl(url) && !isSameOrigin(url, window.location.href);
    } catch {
        // In case of malformed urls, we are swallowing the exception and
        // considering the link as external only, so that we don't end up
        // showing the error on page or stopping the page to get loaded.
        // DTS #1663099
        isExternal = true;
    }
    return isExternal;
}

export function htmlEncode(str: string): string {
    // we can write a slightly faster (30%) version that uses a regexp to find and replace
    // all occurences of [&<>"] by their corresponding HTML entities but this approach
    // uses the DOM implementation, so presumably it covers all input/edge cases.

    const div = document.createElement("div");
    div.appendChild(document.createTextNode(str));

    // The trick we are using here doesnt encode quotes. So we have to replace them using regexp search
    return div.innerHTML.replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

/**
 * Approved list of URL schemes.
 */
const SafeUriSchemes = [
    "http",
    "https",
    "ftp",
    "gopher",
    "mailto",
    "news",
    "telnet",
    "wais",
    "vstfs",
    "tfs",
    "alm",
    "mtm",
    "mtms",
    "mtr",
    "mtrs",
    "mfbclient",
    "mfbclients",
    "test-runner",
    "x-mvwit",
    "onenote",
    "codeflow",
    "file",
    "tel",
    "skype"
];

/**
 * Check if specified URL is safe - i.e. part of an approved list of URL schemes.
 *
 * @param url Url to check.
 * @returns {boolean}
 */
export function isSafeProtocol(url: string): boolean {
    const trimUrl = url.trim();
    const indexOfSchemeDelimiter = trimUrl.indexOf(":");
    if (indexOfSchemeDelimiter >= 0) {
        const scheme = trimUrl.substr(0, indexOfSchemeDelimiter);
        for (const safeUriScheme of SafeUriSchemes) {
            if (Utils_String.equals(scheme, safeUriScheme, true)) {
                return true;
            }
        }
    }

    return false;
}

/**
 * Determines whether the specified URL is absolute or not
 *
 * @param url Url to check.
 * @returns {boolean}
 */
export function isAbsoluteUrl(url: string): boolean {
    if (!url) {
        return false;
    }
    url = url.toLowerCase();
    return url.indexOf("http:") === 0 || url.indexOf("https:") === 0 || url.indexOf("//") === 0;
}
